<template>
	<BaseModal :title="t('title')" v-model="model" noPadding>
		<div class="px-5">
			<div class="text-label-secondary -mt-2 px-3 text-center text-sm">
				{{ t('desc') }}
			</div>
			<BaseCard
				class="mt-5 flex items-center gap-4 py-5 pl-5"
				alt
				:class="{
					'pr-5 *:opacity-30': user.isPremium,
					'pr-2': !user.isPremium
				}"
				@click="openLink(premiumLink)"
				v-hover="{ active: 'scale-95', impact: 'soft' }"
			>
				<div
					class="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#3F3F3F]"
				>
					<PremiumIcon class="h-7 w-7" />
				</div>
				<div>
					<div class="text-label-primary text-sm font-medium">
						{{ t('premium') }}
					</div>
					<div class="text-label-secondary mt-1 text-xs font-medium uppercase">
						{{ user.isPremium ? t('complete') : t('incomplete') }}
					</div>
				</div>
				<div
					class="text-label-tertiary ml-auto inline-flex items-center gap-0.5 text-xs font-medium uppercase"
					v-if="!user.isPremium"
				>
					<div>{{ t('buy') }}</div>
					<CaretRightIcon class="h-6 w-6" />
				</div>
				<CircleCheckIcon v-else class="text-label-primary ml-auto h-5 w-5" />
			</BaseCard>
			<BaseCard
				class="mt-1.5 flex items-center gap-4 py-5 pl-5"
				alt
				:class="{
					'pr-5 *:opacity-30': user.isSubscribed,
					'pr-2': !user.isSubscribed
				}"
				@click="openLink(subscribeLink)"
				v-hover="{ active: 'scale-95', impact: 'soft' }"
			>
				<div
					class="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#3F3F3F]"
				>
					<FollowerIcon class="h-11 w-11" />
				</div>
				<div>
					<div class="text-label-primary text-sm font-medium">
						{{ t('follower') }}
					</div>
					<div class="text-label-secondary mt-1 text-xs font-medium uppercase">
						{{ user.isSubscribed ? t('complete') : t('incomplete') }}
					</div>
				</div>
				<div
					class="text-label-tertiary ml-auto inline-flex items-center gap-0.5 text-xs font-medium uppercase"
					v-if="!user.isSubscribed"
				>
					<div>{{ t('follow') }}</div>
					<CaretRightIcon class="h-6 w-6" />
				</div>
				<CircleCheckIcon v-else class="text-label-primary ml-auto h-5 w-5" />
			</BaseCard>
		</div>
		<BottomActionsModal class="mt-5 pt-4">
			<BaseButton
				size="lg"
				color="primary"
				class="w-full"
				@click="check()"
				:loading="loading"
			>
				{{ t('check') }}
			</BaseButton>
		</BottomActionsModal>
	</BaseModal>
</template>
<script setup lang="ts">
import { ref, watch } from 'vue'
import BaseModal from './BaseModal.vue'
import BaseButton from '../BaseButton.vue'
import BaseCard from '../BaseCard.vue'
import { useI18n } from 'vue-i18n'
import FollowerIcon from '@/assets/icons/follower.svg'
import PremiumIcon from '@/assets/icons/premium.svg'
import { useUser } from '@/stores/user'
import CircleCheckIcon from '@/assets/icons/circle-check.svg'
import BottomActionsModal from '../BottomActionsModal.vue'
import CaretRightIcon from '@/assets/icons/caret-right.svg'
import { client } from '@/api/orpc'
import { useToast } from '@/stores/toast'
import { WebApp } from '@grammyjs/web-app'

const { t } = useI18n({
	messages: {
		en: {
			title: 'one more step...',
			desc: 'There are a couple of conditions to receive the welcome gift',
			premium: 'Premium Subscriber',
			complete: 'complete',
			incomplete: 'incomplete',
			buy: 'buy',
			follow: 'follow',
			follower: 'Rage.Gift Follower',
			check: 'Check',
			notSubscribed:
				'You must subscribe to the Rage.Gift channel to receive the welcome gift.',
			premiumRequired:
				'Telegram Premium is required to receive the welcome gift.',
			success: 'You can now claim your welcome gift!'
		},
		ru: {
			title: 'еще один шаг...',
			desc: 'Есть пара условий для получения приветственного подарка',
			premium: 'Премиум Подписчик',
			complete: 'выполнено',
			incomplete: 'не выполнено',
			buy: 'купить',
			follow: 'перейти',
			follower: 'Подписчик Rage.Gift',
			check: 'проверить',
			notSubscribed:
				'Ты должен подписаться на канал Rage.Gift для получения приветственного подарка.',
			premiumRequired:
				'Для получения приветственного подарка требуется Telegram Premium.',
			success: 'Теперь ты можешь забрать свой приветственный подарок!'
		}
	}
})

const model = defineModel<boolean>()

const close = () => {
	model.value = false
}

const toast = useToast()
const loading = ref(false)

const user = useUser()

const check = async () => {
	if (loading.value) {
		return
	}

	loading.value = true

	const { error, isDefined, data } =
		await client.user.checkPremiumAndChannelSubscription()

	loading.value = false

	if (isDefined) {
		if (error.code === 'UNAUTHORIZED') {
			return // location.reload()
		}
		if (error.code === 'NOT_IN_CHANNEL') {
			toast.add({
				type: 'error',
				text: t('notSubscribed')
			})
			return
		}
	}

	if (error) {
		return console.error(error)
	}

	if (!data) {
		return
	}

	user.setPremiumAndSubscription(data.isPremium, data.isChannelSubscriber)

	if (!data.isPremium) {
		toast.add({
			type: 'error',
			text: t('premiumRequired')
		})
		return
	}

	toast.add({
		type: 'success',
		text: t('success')
	})

	close()
}

const premiumLink = 'https://t.me/PremiumBot'
const subscribeLink = 'https://t.me/ragegift'

const openLink = (href: string) => {
	WebApp.openTelegramLink(href)
}
</script>
